import { urlParamsString } from "utils";
import assert from "utils/assert";
import axios from "../axios";
import RestfulService from "api/common/RestfulService";

// See OpenAPI docs for the AI service:
// http://localhost:7071/docs
// https://aistaging.esg360.io/docs

// Note: This is outside react --edits here dont trigger a re-render.

export const REACT_APP_AI_API_URL = process.env.REACT_APP_AI_API_URL;
// "https://laptop.winterwell.com/v0";

/** see python ai_api_types.py MessageDict */
export class ChatMessage {
  /** @type {string} Set for AI generated messages - unset for user messages */
  trace_id;
  /** @type {string} "user" | "assistant" */
  role;
  /** @type {string} */
  content;
  /** @type {Object} */
  json_content;
  /** @type {string} */
  created_date;
}

export class Thread {
  id;
  /** @type {!ChatMessage[]} */
  messages;
  last_modified_date;
  /** @type {boolean} Used by e.g. Task cancel. If true the agent loop should stop at the next 'breakpoint'. */
  stop_flag;
  /** @type {string} free-form text for display on what the AI is doing */
  status;
  /** @type {string} "running" | "done" | "failed" | "cancelled" */
  state;
  /** @type {number} Incremented for each save. */
  version;
}

export class Task {
  id;
  /** @type {string} draft|queued|running|done|failed|cancelled  */
  status;
  inputs;
  outputs;
  progress;
  error;
}
/**
 * Convenience function to check if a task is closed (done, failed, cancelled)
 * @param {?Task} task
 * @returns {boolean} false if task is unset
 */
export function isTaskClosed(task) {
  if (!task) return false;
  return task.status === "done" || task.status === "failed" || task.status === "cancelled";
}

export class AIContext {
  /** The current URL -- this is set automatically here */
  url;
  /** What page is the user on? */
  app_page_name;
  org_id;
  /** What questionaire-round is the user on? */
  qround_id;
  question_id;
  question_text;
  /** @type {FileInfo[]|string[]} TODO separate ids vs objects */
  files;
  /**
   * The sources that the AI assistant can use to answer the question.
   * Format TBD
   */
  sources;
}
export class ChatResponse {
  content;
  json_content;
  /** @type {!Thread} */
  thread;
  choices;
}

/** See backend-python AISettings.py */
export class AISettings {
  /** @type {string} */
  aiCustomPromptSnippet;
  /** @type {number} Max number of slices (pages) to use for RAG */
  rag_slices_limit;
}
/**
 * The context for the AI assistant. This is set by the current page
 */
let aiContext = {};
/**
 * If the page changes, we need to update the context.
 * This is to check and avoid using old wrong context.
 */
let urlForContext = null;

/**
 * A specification for the files to use for the RAG part of the chat.
 * Note: Field names are Python-style, eg prefer_fileinfo_ids, to match the AI service.
 */
export class FileInfoSpec {
  /** @type {string[]?} */
  prefer_fileinfo_ids;
  /** @type {string?} */
  folder_id;
  /** @type {string[]?} */
  other_fileinfo_ids;
  /** @type {string[]?} */
  exclude_fileinfo_ids;
  /** @type {string[]?} TODO If set - answers from these rounds can be used as sources. */
  round_ids;
  /** @type {string?} Works with round_ids. If set - filter round answers against this.
   * Questions are NOT included by default - set to "*" to include all questions.
   */
  question_filter;
}

// special agents defined in the python AI service
export const AGENT_CANVAS = "canvas";
export const AGENT_CHAT = "chat";

const EXTRA_INSTRUCTIONS = ["extra_instructions", "function_calls"];

/**
 * Our backend agents can add extra instructions to the message. If so: don't show them to the user.
 * @param {string} message
 * @returns {string} message without any extra instructions
 */
export function removeExtraInstructions(message) {
  if (!message) return message;

  let result = message;

  // Remove each tag type
  for (const tag of EXTRA_INSTRUCTIONS) {
    const prefix = `<${tag}>`;
    const suffix = `</${tag}>`;

    let startIndex = result.indexOf(prefix);
    while (startIndex !== -1) {
      const endIndex = result.indexOf(suffix, startIndex + prefix.length);
      if (endIndex === -1) {
        // No closing tag found - remove from start to end
        console.log("removeExtraInstructions - odd: no closer", tag, result);
        result = result.substring(0, startIndex).trimEnd();
        break;
      } else {
        // Remove the tag and its content
        result = result.substring(0, startIndex) + result.substring(endIndex + suffix.length);
      }
      // Look for next occurrence of this tag
      startIndex = result.indexOf(prefix, startIndex);
    }
  }

  result = result.trimEnd();
  return result;
}

/**
 *
 * @param {Object} params
 * @param {!string} params.threadId (sent as thread_id)
 * @param {!string} params.message
 * @param {FileInfoSpec?} params.fileinfoSpec
 * @param {Boolean?} params.saveEdits For report writer: If true, the AI will save edits to the reportdoc.
 * @param {string?} params.speed "LOW" | "MEDIUM" | "HIGH"
 * @returns {Promise<ChatResponse>}
 */
export function postChat({ threadId, message, fileinfoSpec, agent, 
	reportdoc_id, saveEdits, speed, active_round_ids }) {
  console.log("chat", threadId, message, aiContext);
  assert(REACT_APP_AI_API_URL, "REACT_APP_AI_API_URL is not set - check .env file");
  const config = {};
  if (!threadId) threadId = ""; // avoid =undefined
  const params = { thread_id: threadId, message, 
	agent, save_edits: saveEdits, 
	speed, active_round_ids, 
	reportdoc_id };
  const paramsString = urlParamsString(params);
  let data = {
	context: {...aiContext},
	sources: fileinfoSpec
  };
  data.context.url = window.location.href; // always send the current url as context
  return axios.post(`${REACT_APP_AI_API_URL}/chat?${paramsString}`, data, config).then(({ data }) => data);
}


/**
 *
 * @param {!string} threadId (sent as thread_id)
 * @param {!string} message
 * @param {FileInfoSpec?} fileinfoSpec
 * @returns {Promise<ChatResponse>}
 */
export function postReportWriter({ reportdoc_id, message, fileinfoSpec }) {
	const config = {};
	const params = { reportdoc_id, message };
	const paramsString = urlParamsString(params);
	let data = {
		context: {...aiContext},
		sources: fileinfoSpec
	};
	data.context.url = window.location.href; // always send the current url as context
	return axios.post(`${REACT_APP_AI_API_URL}/report-writer?${paramsString}`, data, config).then(({ data }) => data);
  }
  
/**
 * Get `json_content` from the last (most recent) message in the thread.
 * Does not transform anything.
 * @param {!ChatResponse} response
 * @returns {Object}
 */
export function get_json_content(response) {
  if (!response) return null;
  const thread = response.thread || response;
  const messages = thread.messages;
  if (!messages) return null;
  const lastMessage = messages[messages.length - 1];
  if (!lastMessage) return null;
  let json_content = lastMessage.json_content;
  if (json_content === "null") { // paranoia
	console.error("get_json_content - json_content is 'null' string", response);
    json_content = null;
  }
  return json_content;
}

// keys that are not useful in the context, so we remove them
const NOT_USEFUL_IN_CONTEXT = [
  // "questionnaireId", "questionId", // not useful for the actual LLM - but very useful for tracing
  "id",
  "owner",
];

/**
 * Use this to set the context for the AI assistant. E.g. the current page, current question, etc.
 * @param {!AIContext} newContext
 */
export function setAIContext(newContext, urlForNewContext) {
  assert(newContext, "newContext is required");
  aiContext = { ...newContext };
  // remove some un-useful keys
  for (let key of NOT_USEFUL_IN_CONTEXT) {
    delete aiContext[key];
  }
  // the context is for a specific url
  if (!urlForNewContext) {
    urlForNewContext = window.location.href;
  }
  urlForContext = urlForNewContext;
  // debug
  window.aiContext = aiContext;
    console.log("setAIContext", aiContext, urlForContext);
}

/**
 * Update the AI context with a partial new context. Existing context is preserved.
 * @param {*} partialNewContext
 */
export function updateAIContext(partialNewContext) {
  assert(partialNewContext, "partialNewContext is required");
  const newContext = { ...getAIContext(), ...partialNewContext };
  setAIContext(newContext);
}

/**
 * The context for the AI assistant, allowing it give advice based on where the user is.
 * @returns {!AIContext}
 */
export function getAIContext() {
  // To avoid wrong context from a previous page, check if the url has changed.
  // Just the path (not the parameters) -- see https://esg360.atlassian.net/browse/ESG-2689
  // This is vulnerable to if there are "meaningful" parameter changes without a path change.
  // This is not believed to occur in practice in our app.
  // An alternative would be to have an ignore-list (or conversely a watch-list) of specific parameters
  if (urlForContext !== window.location.href) {
    // Check if the path has changed
    const newPath = new URL(window.location.href).pathname;
    const oldPath = urlForContext && new URL(urlForContext).pathname;
    if (newPath !== oldPath) {
      console.log("urlForContext changed - clearing context", newPath, oldPath);
      setAIContext({});
    } else {
      console.log("url change but not a path change - AI context is unchanged", urlForContext, window.location.href);
    }
  }
  return aiContext;
}

/**
 * Note: Only one at  a time!
 * @type {function(event: any)}
 */
let eventListener;
let urlForEventListener;
/**
 * Use this so a page can listen for events from the AI assistant.
 * Note: There's only one listener at a time! Use-case: so the answer page can listen to chat events.
 * @param {function} listener input: (event: {message: {content: string, json_content: any})
 */
// Hm: Could use redux here -- though this adds a safety check that the listener is set from the same page.
export function setAIEventListener(listener) {
  eventListener = listener;
  urlForEventListener = window.location.href;
}

/**
 * Answers have to be applied in the question/answer page, which can be done in two ways:
 * 1. Direct wiring, from the AI Draft tab or activity list -- which are hooked up to the question/answer page.
 * 2. Fron AI Assist chat, if it's made a new candidate answer -- it can send an event for the question page to apply.
 */
export const ACTION_applyAnswer = "applyAnswer";

/**
 * See setAIEventListener()
 * @param {Object} event input: {message: {content: string, json_content: any}}
 */
export function emitAIEvent(event) {
  if (urlForEventListener !== window.location.href) {
    console.log("urlForEventListener changed - clearing listener", urlForEventListener, window.location.href);
    setAIEventListener(null);
  }
  if (eventListener) {
    eventListener(event);
  } else {
	if (event.action === ACTION_applyAnswer) {
		throw new Error("No event listener set - cannot apply answer", event);
	}
    console.error("no event listener set - event ignored", event);
  }
}



/**
 * Get the AI source for a given fileinfo
 * @param {!FileInfo} fileinfo
 * @returns {Promise<AISource>}
 */
export function apiGetAISource(fileinfo) {
  assert(fileinfo.id, "fileinfo.id is required", fileinfo);
  assert(REACT_APP_AI_API_URL, "REACT_APP_AI_API_URL is not set - check .env file");
  const config = {};
  const paramsString = urlParamsString({ q: "fileinfoId:" + fileinfo.id });
  return axios.get(`${REACT_APP_AI_API_URL}/sources?${paramsString}`, config).then(({ data }) => data[0]);
}

/**
 * Request the AI to process (all) files.
 * Note: This does not trigger question answering.
 * @param {string[]?} fileinfoIds If unset sync all
 * @param {boolean?} soft If true, the source will only be updated if the AI records are out of date.
 * @returns
 */
export function postSourcesUpdate(fileinfoIds, soft) {
  assert(Array.isArray(fileinfoIds), "fileinfoIds array is required");
  assert(fileinfoIds.length, "fileinfoIds array is empty");
  const config = {};
  const paramsString = urlParamsString({ fileinfo_ids: fileinfoIds, soft });
  return axios.post(`${REACT_APP_AI_API_URL}/sources/update?${paramsString}`, config).then(({ data }) => data);
}

export function deleteAISourceByAISourceId(aisourceId) {
  assert(aisourceId, "aisourceId is required");
  const config = {};
  return axios
    .delete(`${REACT_APP_AI_API_URL}/source/${encodeURIComponent(aisourceId)}`, config)
    .then(({ data }) => data);
}
export function deleteAISourceByFileInfoId(fileinfoId) {
  assert(fileinfoId, "fileinfoId is required");
  const config = {};
  return axios
    .delete(`${REACT_APP_AI_API_URL}/source?fileinfo_id=${encodeURIComponent(fileinfoId)}`, config)
    .then(({ data }) => data);
}

export function apiGetThreads({ userId, questionId, reportDocId }) {
  assert(REACT_APP_AI_API_URL, "REACT_APP_AI_API_URL is not set - check .env file");
  const config = {};
  const paramsString = urlParamsString({ userId, question_id: questionId, reportdoc_id: reportDocId });
  return axios.get(`${REACT_APP_AI_API_URL}/threads?${paramsString}`, config).then(({ data }) => data);
}

export function apiGetThread(threadId) {
  assert(threadId, "threadId is required");
  const config = {};
  return axios.get(`${REACT_APP_AI_API_URL}/thread/${encodeURIComponent(threadId)}`, config).then(({ data }) => data);
}

export function apiPostThreadUpdate(thread) {
//   console.warn("apiPostThreadUpdate", thread);
  const config = {};
  return axios
    .post(`${REACT_APP_AI_API_URL}/thread/${encodeURIComponent(thread.id)}`, thread, config)
    .then(({ data }) => data);
}

/**
 * Request the AI to answer questions.
 *
 * Note: call invalidateQueries("taskService.list") after calling this to refresh the task list.
 * @param {Object} params
 * @param {string[]} params.question_ids if blank - do all!
 * @param {FileInfoSpec?} params.fileinfoSpec
 * @param {string} params.query ??
 * @param {string} params.overwrite AI|all|{stage}
 * @param {boolean} params.draft If true, the AI will only create draft answers
 * @returns {Promise<Task>}
 */
export function postQuestionsAnswerJob({ questionnaireId, questionIds, fileinfoSpec, query, overwrite, draft }) {
  assert(typeof questionnaireId === "string", "questionnaireId is required", questionnaireId);
  assert(REACT_APP_AI_API_URL, "REACT_APP_AI_API_URL is not set - check .env file");
  const config = {};
  // TODO use body in case params get too long
  const sources = fileinfoSpec;
  const qaTaskInputs = {
    questionnaire_id: questionnaireId,
    question_ids: questionIds,
    query,
    overwrite,
    draft,
	sources
  };
  return axios.post(`${REACT_APP_AI_API_URL}/question-answer`, qaTaskInputs, config).then(({ data }) => data);
}

class Feedback {
  /** @type {string} */
  comment;
  /** @type {string} */
  user_id;
  /** @type {string} */
  user_role;
  /** @type {string} */
  created_date;
  /** @type {string} */
  last_modified_date;
  /** @type {string} */
  user_action;
  /** @type {number} */
  score;
  /** @type {boolean} */
  positive;
  /** @type {boolean} */
  applied;
  /** @type {string} If the user edited the message, this is their 'improved' edited message */
  edited;
}

/**
 *
 * @param {!string} threadId
 * @param {!string} traceId
 * @param {!Feedback} feedback
 * @returns {Promise<Thread>}
 */
export function postFeedback({ threadId, traceId, msgId, feedback }) {
  assert(threadId, "threadId is required");
  assert(feedback, "feedback is required");
  const paramsString = urlParamsString({ thread_id: threadId, trace_id: traceId, msg_id: msgId });
  return axios.post(`${REACT_APP_AI_API_URL}/feedback?${paramsString}`, feedback).then(({ data }) => data);
}

export function getFeedback({ threadId, traceId, msgId }) {
  assert(threadId, "threadId is required");
  assert(msgId || traceId, "msgId or traceId is required");
  const paramsString = urlParamsString({ thread_id: threadId, trace_id: traceId, msg_id: msgId });
  return axios.get(`${REACT_APP_AI_API_URL}/feedback?${paramsString}`).then(({ data }) => data);
}

export function getWeaviateQuestion(weaviateQuestionId) {
  assert(weaviateQuestionId, "weaviateQuestionId is required");
  return axios
    .get(`${REACT_APP_AI_API_URL}/question/${encodeURIComponent(weaviateQuestionId)}`)
    .then(({ data }) => data);
}

export function getPromptList() {
  return axios.get(`${REACT_APP_AI_API_URL}/prompt`).then(({ data }) => data);
}
export function getPrompt(promptId) {
  assert(promptId, "promptId is required");
  return axios.get(`${REACT_APP_AI_API_URL}/prompt/${encodeURIComponent(promptId)}`).then(({ data }) => data);
}

/**
 * Note: taskService.list should use the parameter `round_id`
 */
export const taskService = new RestfulService(`${REACT_APP_AI_API_URL}/task`);

export function apiGetTaskRunnerStatus() {
  const config = {};
  return axios.get(`${REACT_APP_AI_API_URL}/task_runner`, config).then(({ data }) => data);
}
taskService.cancel = (taskId) => {
  const config = {};
  // Note: slightly different endpoint to the restful /task methods
  return axios
    .post(`${REACT_APP_AI_API_URL}/task_cancel/${encodeURIComponent(taskId)}`, config)
    .then(({ data }) => data);
};

/**
 * HACK convert non user friendly backend status messages to something more user friendly
 * @param {*} status 
 * @returns 
 */
export function displayThreadStatus(status) {
  if (!status) return status;
  if (status.includes("check")) {
	// return "Running checks";
  }
  if (status === "failed") {
	return null; // show errors somewhere else
  }
  // HACK a crude way to avoid showing status from the previous message
  if (status === "done") return null;
  if (status === "apology") return null;  
  return status;
}

export function round_as_source_sync_questions(roundId) {
  assert(roundId, "roundId is required");
  const config = {};
  const params = { round_id: roundId };
  return axios.post(`${REACT_APP_AI_API_URL}/round_as_source_sync_questions?${urlParamsString(params)}`, config).then(({ data }) => data);
}